/**
 * Machine-local camera preference for the counter's loyalty-card scanner.
 *
 * A camera is physical to a given terminal, exactly like a receipt printer
 * (see receiptPrinter.ts): a Surface till has a front AND a rear camera and
 * the rear one is the useful one — the customer holds their wallet card up to
 * the back of the machine — while the next tenant's till has a single webcam
 * whose deviceId means nothing here. So the choice belongs on the machine, not
 * in the shared tenant database. localStorage in the desktop app is per-origin
 * (the local server) and is wiped when the shell is re-pointed at another
 * tenant, so it never bleeds across restaurants.
 *
 * deviceIds are also not stable forever — they rotate when the browser's
 * storage is cleared and vanish when a USB camera is unplugged — so every
 * consumer must treat a saved id as a hint and fall back gracefully. See the
 * OverconstrainedError retry in CounterScanLoyaltyCard.
 */

const KEY = 'bhookly-scanner-camera'

export interface ScannerCameraPrefs {
  /** MediaDeviceInfo.deviceId of the camera to use, '' => let the browser pick. */
  deviceId: string
  /**
   * Whether opening the scan panel should start the viewfinder immediately.
   * Defaults to true; set false when staff explicitly close the camera on this
   * machine (a till that only ever uses its USB scanner shouldn't have a
   * webcam light come on with every guest lookup).
   */
  autoStart: boolean
}

const DEFAULTS: ScannerCameraPrefs = { deviceId: '', autoStart: true }

export function getScannerCamera(): ScannerCameraPrefs {
  if (typeof window === 'undefined') return { ...DEFAULTS }
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return { ...DEFAULTS }
    const parsed = JSON.parse(raw) as Partial<ScannerCameraPrefs> | null
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULTS }
    return {
      deviceId: typeof parsed.deviceId === 'string' ? parsed.deviceId : '',
      autoStart: parsed.autoStart !== false,
    }
  } catch {
    return { ...DEFAULTS }
  }
}

function write(prefs: ScannerCameraPrefs): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs))
  } catch {
    // localStorage unavailable/full — non-fatal; the scanner just forgets the
    // choice and falls back to the browser's default camera next time.
  }
}

/** Pin the scanner to a specific camera on this machine ('' clears the pin). */
export function setScannerCameraId(deviceId: string): void {
  write({ ...getScannerCamera(), deviceId: deviceId || '' })
}

/** Remember whether the viewfinder should auto-start on this machine. */
export function setScannerAutoStart(autoStart: boolean): void {
  write({ ...getScannerCamera(), autoStart })
}

/** Forget everything — used when a saved deviceId turns out to be dead. */
export function clearScannerCamera(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}

/**
 * Video input devices on this machine.
 *
 * Labels are empty until camera permission has been granted at least once, so
 * callers that want readable names should enumerate AFTER getUserMedia has
 * resolved. Returns [] on any failure — an empty list is the signal "this
 * machine has no camera", which is how the counter decides not to auto-start.
 */
export async function listVideoInputs(): Promise<MediaDeviceInfo[]> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return []
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((d) => d.kind === 'videoinput')
  } catch {
    return []
  }
}

/** Human label for a camera, falling back to a positional name pre-permission. */
export function cameraLabel(device: MediaDeviceInfo, index: number): string {
  return device.label?.trim() || `Camera ${index + 1}`
}

/**
 * Whether this device can decode a QR from its camera in-browser.
 *
 * Deliberately does NOT test for `BarcodeDetector`. Chrome ships that API on
 * Android, macOS and ChromeOS only — on Chrome for Windows and Linux, and
 * therefore inside the Electron shell on every restaurant PC we deploy to, it
 * is undefined. Gating on it hid camera scanning from the entire till fleet
 * while working fine on the developer's Mac. The counter now falls back to a
 * bundled jsQR decoder, so the only real requirement is a camera stream, and
 * getUserMedia needs a secure context — https, or the loopback origin the
 * desktop shell serves from.
 */
export function cameraScanSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext !== false &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  )
}

/** Touch-primary device (phone/tablet) — where focusing an input opens a keyboard. */
export function isTouchPrimary(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(pointer: coarse)').matches
}
